import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from "../../../../../../../hooks/use-search";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, IconButton, Input, MenuItem, Popover, Stack, Typography} from "@mui/material";
import {ArrowBackIosNewRounded, ArrowForwardIosRounded} from "@mui/icons-material";
import {createUUId} from "../../../../../../../../core/services/utils/utils";
import {DashboardWidgetData, DashboardWidgetOrderByField} from "../../types";
// @ts-ignore
import {ReactComponent as SortIcon} from "../../assets/sort.svg";

interface DashboardChartFooterProps {
    data: DashboardWidgetData['data'];
    isLoading: boolean;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    orderByFields?: DashboardWidgetOrderByField[];
    hasPagination?: boolean;
}

const DashboardWidgetFooter: React.FC<DashboardChartFooterProps> = ({data, isLoading, setPaginationInfo, paginationInfo, orderBy, setOrderBy, orderByFields, hasPagination}) => {
    const [popover, setPopover] = useState(null);
    const [page, setPage] = useState<string | number | undefined>(paginationInfo?.currentPage);
    const id = useMemo(() => createUUId(), []);

    /**
     * Updates the page state when the paginationInfo changes
     */
    useEffect(() => {
        setPage(paginationInfo?.currentPage);
    }, [paginationInfo?.currentPage]);


    /**
     * Closes the popover
     */
    const closePopover = useCallback(() => setPopover(null), []);

    /**
     * Opens the popover
     * @param {Event} e
     */
    const openPopover = useCallback((e) => {
        if (!orderByFields?.length)
            return;
        setPopover(e.currentTarget)
    }, [orderByFields]);

    const orderByFieldsWithDescending = useMemo(() => {
        if (!orderByFields) return [];
        return orderByFields
            .map(field => [
                {...field, descending: false},
                {...field, descending: true},
            ])
            .flat();
    }, [orderByFields]);

    const hasAnything = useMemo(() => hasPagination || (orderByFields?.length ?? 0) > 0, [hasPagination, orderByFields]);

    /**
     * Handles the page input blur event
     */
    const onPageBlur = useCallback((e) => {
        let pageNum = Number(e.target.value);
        if (isNaN(pageNum) || pageNum < 1) {
            pageNum = 1
        }
        setPage(pageNum);
        if (!!paginationInfo) {
            setPaginationInfo(prevState => ({
                ...prevState!,
                currentPage: Math.max(1, pageNum),
            }));
        }
    }, [paginationInfo, setPaginationInfo]);

    if (!hasAnything) {
        return null;
    }

    return (
        <>
            <Stack direction="row" alignItems="center" sx={{paddingBlockStart: 2}} gap={0.5} justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-start">
                    {
                        (orderByFields?.length ?? 0) > 0 &&
                        <Card
                            variant={'elevation'}
                            onClick={openPopover}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: {xs: '100%', md: 'auto'},
                                boxShadow: 'none',
                                backgroundColor: 'secondary.light',
                                borderRadius: 1,
                                paddingInline: 1,
                                paddingBlock: 0.5,
                            }}
                        >
                            <Typography variant="caption">
                                Order By:
                            </Typography>
                            <Typography variant="caption" color={orderBy ? 'text.primary' : 'text.secondary'}>
                                <Typography
                                    component="span"
                                    variant={'caption'}
                                    fontWeight={700}
                                    color={'text.primary'}
                                    marginInlineEnd={0.5}
                                >
                                    {orderByFieldsWithDescending.find(field => field.name === orderBy?.field)?.title ?? orderBy?.field ?? "None"}
                                </Typography>
                                {!orderBy ? "" : orderBy.descending ? `Descending` : `Ascending`}
                            </Typography>
                            {
                                !!orderBy &&
                                <Box
                                    sx={(theme) => ({
                                        color: orderBy ? theme.palette.primary.main : theme.palette.text.secondary,
                                        transition: theme.transitions.create(['opacity', 'color', 'background-color', 'transform']),
                                        opacity: !orderBy ? 0.5 : 1,
                                        transform: orderBy?.descending ? 'rotate(180deg)' : 'rotate(0deg)',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    })}
                                >
                                    <SortIcon/>
                                </Box>
                            }
                        </Card>
                    }
                </Stack>
                {
                    hasPagination &&
                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-end">
                        <Typography variant="caption" component={'div'}>
                            Page <Input
                            disableUnderline
                            value={page}
                            size="small"
                            type="number"
                            onChange={(e) => setPage(e.target.value)}
                            onBlur={onPageBlur}
                            placeholder="1"
                            slotProps={{
                                input: {min: 1},
                            }}
                            sx={{
                                input: {
                                    textAlign: 'center',
                                },
                                maxWidth: 50,
                                width: 'fit-content',
                                display: 'inline-block',
                                ...(isLoading && {
                                    color: 'text.disabled',
                                }),
                            }}
                        />
                        </Typography>
                        <IconButton
                            color={'paper.primary'}
                            size={'small'}
                            disabled={isLoading || paginationInfo?.currentPage === 1}
                            onClick={() => {
                                if (paginationInfo) {
                                    setPaginationInfo(prevState => ({
                                        ...prevState!,
                                        currentPage: Math.max(1, paginationInfo.currentPage - 1),
                                    }));
                                }
                            }}
                        >
                            <ArrowBackIosNewRounded fontSize={'small'} sx={{padding: 0.25}}/>
                        </IconButton>
                        <IconButton
                            color={'paper.primary'}
                            size={'small'}
                            disabled={isLoading || !data.length}
                            onClick={() => {
                                if (paginationInfo) {
                                    setPaginationInfo(prevState => ({
                                        ...prevState!,
                                        currentPage: paginationInfo.currentPage + 1,
                                    }));
                                }
                            }}
                        >
                            <ArrowForwardIosRounded fontSize={'small'} sx={{padding: 0.25}}/>
                        </IconButton>
                    </Stack>
                }
            </Stack>
            <Popover
                id={!!popover ? id : undefined}
                elevation={2}
                open={!!popover}
                onClose={closePopover}
                anchorReference={'anchorEl'}
                anchorEl={popover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                {
                    orderByFieldsWithDescending?.map(field => (
                        <MenuItem
                            key={field.name + (field.descending ? '_desc' : '_asc')}
                            selected={orderBy?.field === field.name && orderBy?.descending === field.descending}
                            onClick={() => {
                                closePopover();
                                setOrderBy?.({descending: field.descending, field: field.name,});
                            }}
                        >
                            <Typography
                                component={'span'}
                                variant={'body2'}
                                fontWeight={700}
                                color={'text.primary'}
                            >
                                {field.title ?? ""}
                            </Typography> {
                            field.descending ? <SortIcon style={{transform: 'rotate(180deg)'}}/> : <SortIcon/>
                        } <Typography variant={'caption'} fontWeight={600} color={'primary.main'} marginInlineStart={0.5}>
                            {field.descending ? " (Descending)" : " (Ascending)"}
                        </Typography>
                        </MenuItem>
                    ))
                }
            </Popover>
        </>
    );
}

export default DashboardWidgetFooter;